import {
  CATALOG_BY_ID,
  type MdmCommandDefinition,
  type MdmCommandId,
} from '@staff-app/shared-types';
import {
  getLayoutEntry,
  MDM_ACTION_LAYOUT,
  type MdmActionLayoutEntry,
} from '@/features/customers/components/actions/MdmActionLayout';
import { readFeatureFlag } from '@/features/customers/components/actions/MdmActionHandlers';
import type { LiveDeviceStatus } from '@/features/mdm-devices/hooks/useDeviceHeartbeats';

/**
 * Commands the My Testing Devices action bar may render (phase 3). Still intersected with the device's
 * own `availableCommands`. Platform-api room ops (Sync / vendor admin / issues) stay out — no testing
 * proxy exists for them.
 */
export const TESTING_ACTION_ALLOWLIST: readonly MdmCommandId[] = [
  // phase 2 — read-mostly / low blast radius
  'IDENTIFY_SCREEN',
  'MDM_REBOOT',
  'MDM_REFRESH',
  'MDM_CAPTURE_SCREENSHOT',
  'MDM_TOGGLE_REMOTE_CONTROL',
  'MDM_OPEN_REMOTE_VIEWER',
  'MDM_GET_USED_STORAGE',
  'MDM_VIEW_DEVICE_INFO',
  'MDM_COMMAND_HISTORY',
  // phase 3 — dialog-driven device changes
  'MDM_SET_TIMEZONE',
  'MDM_WIFI_MANAGEMENT',
  'MDM_INSTALL_OPTISIGNS_VERSION',
  'MDM_INSTALL_APK',
  'MDM_OTA_UPDATE',
  'MDM_HDMI_CEC',
  'MDM_RS232',
  'MDM_ENABLE_AUTOUPDATE',
  'MDM_DISABLE_AUTOUPDATE',
  'MDM_EXECUTE_REMOTE_COMMAND',
];

/**
 * The customer layout re-ordered to scio3's MDM drawer priority, with Execute Remote Commands tinted
 * destructive (it stays last in More, but on a real testing device it is the one free-form command, so
 * it reads as dangerous).
 *
 * Toolbar priority follows scio3 (`MdmActionLayout` orders): Identify · Remote Control · Reboot ·
 * Refresh & Relaunch · Remote · Take screenshot. scio3 shows as many of them as FIT and collapses the
 * rest into More; `TestingActionBar` does the same fit over these toolbar entries.
 */
export const TESTING_ACTION_LAYOUT: Record<MdmCommandId, MdmActionLayoutEntry> = {
  ...MDM_ACTION_LAYOUT,
  IDENTIFY_SCREEN: { ...MDM_ACTION_LAYOUT.IDENTIFY_SCREEN, order: 10 },
  MDM_TOGGLE_REMOTE_CONTROL: {
    ...MDM_ACTION_LAYOUT.MDM_TOGGLE_REMOTE_CONTROL,
    order: 40,
  },
  MDM_REBOOT: { ...MDM_ACTION_LAYOUT.MDM_REBOOT, order: 60 },
  MDM_REFRESH: { ...MDM_ACTION_LAYOUT.MDM_REFRESH, order: 70 },
  MDM_OPEN_REMOTE_VIEWER: {
    ...MDM_ACTION_LAYOUT.MDM_OPEN_REMOTE_VIEWER,
    order: 80,
  },
  MDM_CAPTURE_SCREENSHOT: {
    ...MDM_ACTION_LAYOUT.MDM_CAPTURE_SCREENSHOT,
    order: 90,
  },
  MDM_EXECUTE_REMOTE_COMMAND: {
    ...MDM_ACTION_LAYOUT.MDM_EXECUTE_REMOTE_COMMAND,
    variant: 'destructive',
  },
};

/** Toolbar entries whose order differs from the customer layout (scio3 priority). */
export const TESTING_REORDERED_TOOLBAR: readonly MdmCommandId[] = [
  'IDENTIFY_SCREEN',
  'MDM_TOGGLE_REMOTE_CONTROL',
  'MDM_REBOOT',
  'MDM_REFRESH',
  'MDM_OPEN_REMOTE_VIEWER',
  'MDM_CAPTURE_SCREENSHOT',
];

const ALLOWED = new Set<string>(TESTING_ACTION_ALLOWLIST);

export interface TestingActionCommands {
  toolbar: MdmCommandDefinition[];
  overflow: MdmCommandDefinition[];
}

/**
 * `detail.availableCommands` (open-api's resolution) ∩ the phase allowlist, decoded through the
 * catalog and split by the CUSTOMER layout. Two device-state touches the catalog leaves to the row:
 *  - Remote Control reads as "Disable Remote Control" while `feature.remoteControl` is on;
 *  - "Remote" (open the viewer) only shows once Remote Control is on — the catalog predicate cannot
 *    see the per-device toggle;
 *  - of the auto-update pair only the relevant one shows (scio3): Enable while
 *    `feature.isDisableAutoUpdate` is set, Disable otherwise.
 */
export function resolveTestingCommands(
  availableCommands: readonly string[] | null | undefined,
  feature: Record<string, unknown> | null | undefined,
): TestingActionCommands {
  const remoteOn = readFeatureFlag(feature, 'REMOTE_CONTROL');
  const autoUpdateDisabled = Boolean(feature?.isDisableAutoUpdate);
  const defs: MdmCommandDefinition[] = [];
  const seen = new Set<string>();
  for (const id of availableCommands ?? []) {
    if (!ALLOWED.has(id) || seen.has(id)) continue;
    seen.add(id);
    const def = CATALOG_BY_ID[id as MdmCommandId];
    if (!def) continue;
    if (def.id === 'MDM_OPEN_REMOTE_VIEWER' && !remoteOn) continue;
    if (def.id === 'MDM_DISABLE_AUTOUPDATE' && autoUpdateDisabled) continue;
    if (def.id === 'MDM_ENABLE_AUTOUPDATE' && !autoUpdateDisabled) continue;
    if (def.id === 'MDM_TOGGLE_REMOTE_CONTROL' && remoteOn) {
      defs.push({ ...def, i18nKey: 'Disable Remote Control' });
      continue;
    }
    defs.push(def);
  }
  const byOrder = (a: MdmCommandDefinition, b: MdmCommandDefinition) =>
    getLayoutEntry(a.id, TESTING_ACTION_LAYOUT).order -
    getLayoutEntry(b.id, TESTING_ACTION_LAYOUT).order;
  return {
    toolbar: defs
      .filter((d) => getLayoutEntry(d.id, TESTING_ACTION_LAYOUT).slot === 'toolbar')
      .sort(byOrder),
    overflow: defs
      .filter((d) => getLayoutEntry(d.id, TESTING_ACTION_LAYOUT).slot === 'overflow')
      .sort(byOrder),
  };
}

/**
 * Why a screenshot can't be taken right now, or null when it can. Mirrors the checks staff-api's
 * `captureDeviceScreenshot` runs before it sends anything, so the button says so up front instead of
 * the capture coming back `unsupported_device` / `blocked`.
 */
export function screenshotBlockedReason(input: {
  utilsVersion: string | null | undefined;
  liveStatus: LiveDeviceStatus | 'stale' | undefined;
}): string | null {
  if (!input.utilsVersion) {
    return 'Screen capture needs the OptiSigns utils agent, which this device does not have';
  }
  if (input.liveStatus === 'offline') return 'The device is offline, so a capture cannot reach it';
  if (input.liveStatus === 'hdmiOff') return 'The display output is off (HDMI off)';
  return null;
}
